import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ChevronRight, CheckCircle, Building2, Briefcase, MapPin, Clock } from 'lucide-react';

export const CandidateCard = ({ candidate, isAuthenticated }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/candidate/${candidate.id}`);
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const getDirectImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1589220286904-3dcef62c68ee?w=400&h=300&fit=crop';
    
    // Handle relative API paths (persistent across deployments)
    if (url.startsWith('/api/')) {
      return `${BACKEND_URL}${url}`;
    }
    
    // Handle old full URLs with /api/images/
    if (url.includes('/api/images/')) {
      const imageId = url.split('/api/images/')[1];
      return `${BACKEND_URL}/api/images/${imageId}`;
    }
    
    let fileId = null;
    if (url.includes('drive.google.com/open?id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    } else if (url.includes('drive.google.com/file/d/')) {
      fileId = url.split('/d/')[1]?.split('/')[0];
    } else if (url.includes('drive.google.com/uc?id=')) {
      fileId = url.split('id=')[1]?.split('&')[0];
    }
    
    if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
    return url;
  };

  const photoUrl = getDirectImageUrl(candidate.photo_url);

  // Determine availability from notice period
  const isImmediatelyAvailable = candidate.notice_period?.toLowerCase().includes('immediate') || 
    candidate.notice_period?.toLowerCase().includes('0') ||
    candidate.notice_period === '0 days';

  return (
    <Card 
      className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-hfs-navy/30 transition-all duration-300 group cursor-pointer relative h-full"
      onClick={handleClick}
      data-testid={`candidate-card-${candidate.id}`}
    >
      {/* Badges */}
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
        {candidate.is_placed && (
          <Badge className="bg-green-500 text-white px-2 py-1 flex items-center gap-1 shadow-md text-xs">
            <CheckCircle className="h-3 w-3" />
            Hired
          </Badge>
        )}
        {isImmediatelyAvailable && !candidate.is_placed && (
          <Badge className="bg-blue-500 text-white px-2 py-1 flex items-center gap-1 shadow-md text-xs">
            <Clock className="h-3 w-3" />
            Immediate
          </Badge>
        )}
      </div>
      
      {/* Horizontal Layout - Photo on left, Details on right */}
      <div className="flex h-full">
        {/* Photo - Fixed width, full height */}
        <div className="w-32 md:w-36 flex-shrink-0 overflow-hidden bg-slate-100 relative">
          <img 
            src={photoUrl} 
            alt={candidate.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1589220286904-3dcef62c68ee?w=400&h=300&fit=crop';
            }}
          />
        </div>
        
        {/* Content - Flex grow */}
        <div className="flex-1 p-4 flex flex-col min-h-[200px]">
          {/* Name */}
          <h3 className="font-heading font-semibold text-base text-slate-900 mb-2 group-hover:text-hfs-navy transition-colors line-clamp-1 pr-16">
            {candidate.name}
          </h3>
          
          {/* Info Grid */}
          <div className="space-y-1.5 mb-3 flex-1">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Briefcase className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="truncate">{candidate.years_of_experience || 'Experience N/A'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="truncate">{candidate.current_organization || 'Organization N/A'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="h-3.5 w-3.5 text-slate-400 flex-shrink-0 font-semibold text-[10px]">₹</span>
              <span className="truncate">
                {candidate.expected_ctc ? (
                  <>Expected CTC: {candidate.expected_ctc} <span className="text-slate-400">(Negotiable)</span></>
                ) : (
                  'CTC N/A'
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <span className="truncate">{candidate.preferred_work_mode || 'Work Mode N/A'}</span>
            </div>
          </div>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {candidate.notice_period && !isImmediatelyAvailable && (
              <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {candidate.notice_period}
              </span>
            )}
            {candidate.willing_to_relocate?.toLowerCase().includes('yes') && (
              <span className="text-[10px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                Open to Relocate
              </span>
            )}
          </div>
          
          {/* Placed Company Info */}
          {candidate.is_placed && candidate.placed_company_name && (
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-100 mb-3">
              <Building2 className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span className="text-xs text-green-700 font-medium truncate">
                Hired at {candidate.placed_company_name}
              </span>
            </div>
          )}
          
          {/* View Details Button */}
          <Button
            variant="ghost"
            className="w-full justify-between text-hfs-navy hover:text-hfs-navy-dark hover:bg-hfs-navy/5 transition-colors text-xs h-9 mt-auto"
            data-testid={`view-details-btn-${candidate.id}`}
          >
            View Profile Details
            <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
